// sim/projectsData.ts — REGION-SCALE COLONY PROJECTS: the long-term build goals a
// colony pours WORKERS + RESOURCES + TIME into for a PERMANENT payoff. Pure data +
// enums (no behaviour). `src/sim/projects.ts` is the deterministic system that runs
// them; `web/game.ts` surfaces them to the player as the standing long-term objective.
//
// A project is themed to a REGION (flavour + UI grouping). The hard start gate is a
// colony-POPULATION threshold (a region-scale undertaking needs hands) — kept on
// deterministic colony state so a saved game replays identically regardless of the
// region/territory layer (which lives above the sim).

import { REGION } from './regions.ts';
import type { Region } from './regions.ts';

/** The buildable projects (dense indices; COUNT sibling). */
export const PROJECT = {
  CompostNetwork: 0,
  PondHarvest: 1,
  TunnelSystem: 2,
  FungalSuperfarm: 3,
} as const;
export type Project = (typeof PROJECT)[keyof typeof PROJECT];
export const PROJECT_COUNT = 4;

/**
 * The KIND of permanent bonus a completed project confers. Every project still pays
 * its `foodDividend` each cadence (the universal, byte-identical payout); `bonusType`
 * additionally LABELS the distinct, themed effect that consuming systems can read via
 * `ColonyProjectSystem.bonusTypeOf` / `effectOf` and apply later. This is a pure data
 * seam — adding/reading a type changes no default behaviour until a consumer wires it.
 */
export const BONUS_TYPE = {
  /** The baseline: a per-cadence food deposit (what every project already pays today). */
  FoodDividend: 0,
  /** A standing defensive edge for the colony (e.g. sheltered hauling, raid resistance). */
  Defense: 1,
  /** A higher population ceiling (more hands the colony can sustain). */
  PopCap: 2,
  /** A raised gene-drop / mutagen rate (faster evolution opportunities). */
  GeneDropRate: 3,
} as const;
export type BonusType = (typeof BONUS_TYPE)[keyof typeof BONUS_TYPE];

/** Per-colony lifecycle of one project. */
export const PROJECT_STATUS = {
  /** Not yet begun — startable once the gates are met. (Default state = 0.) */
  Available: 0,
  /** Under construction — accruing labour + food each cadence. */
  Active: 1,
  /** Finished — its permanent bonus pays out every cadence, forever. */
  Complete: 2,
} as const;
export type ProjectStatus = (typeof PROJECT_STATUS)[keyof typeof PROJECT_STATUS];

export interface ProjectDef {
  readonly id: Project;
  readonly name: string;
  /** Themed region (flavour + UI grouping; not a hard sim gate). */
  readonly region: Region;
  /** Min colony population required to BEGIN — a region-scale build needs hands. */
  readonly minPopulation: number;
  /** Total worker-progress to finish (encodes the WORKERS + TIME cost). */
  readonly laborNeeded: number;
  /** Total food the build consumes over its life (the RESOURCES cost). */
  readonly foodCost: number;
  /** PERMANENT BONUS: food deposited into the colony every project cadence, forever. */
  readonly foodDividend: number;
  /**
   * The KIND of distinct permanent effect this project confers (consumers read it via
   * the system's accessors). Orthogonal to `foodDividend`, which every project still
   * pays; this just differentiates what ELSE a finished project does. See BONUS_TYPE.
   */
  readonly bonusType: BonusType;
  /** One-time leaderboard value awarded on completion (standing). */
  readonly leaderboardValue: number;
  /** EVOLUTION OPPORTUNITY: Mutation Energy granted on completion (fuels boxes/ascension). */
  readonly energyReward: number;
  readonly blurb: string;
}

// Ordered by id (0..PROJECT_COUNT-1) so PROJECT_DEFS[project] is the definition.
// Costs/rewards scale up the roster: later projects are bigger commitments with
// bigger, compounding payoffs — a deliberate long-horizon ladder.
export const PROJECT_DEFS: readonly ProjectDef[] = [
  {
    id: PROJECT.CompostNetwork,
    name: 'Compost Processing Network',
    region: REGION.CompostHeap,
    minPopulation: 18,
    laborNeeded: 900,
    foodCost: 220,
    foodDividend: 5,
    bonusType: BONUS_TYPE.FoodDividend, // a decay-fed food engine: its edge IS the dividend.
    leaderboardValue: 120,
    energyReward: 40,
    blurb: 'A web of decay-chambers that turns the compost heap into a steady food engine.',
  },
  {
    id: PROJECT.PondHarvest,
    name: 'Pond Harvest Network',
    region: REGION.Pond,
    minPopulation: 24,
    laborNeeded: 1300,
    foodCost: 320,
    foodDividend: 7,
    bonusType: BONUS_TYPE.PopCap, // rafts + shoreline runs feed more mouths: a higher ceiling.
    leaderboardValue: 170,
    energyReward: 60,
    blurb: 'Harvest rafts and shoreline runs that reap the pond’s algae and drowned prey.',
  },
  {
    id: PROJECT.TunnelSystem,
    name: 'Underground Tunnel System',
    region: REGION.Backyard,
    minPopulation: 30,
    laborNeeded: 1700,
    foodCost: 260,
    foodDividend: 6,
    bonusType: BONUS_TYPE.Defense, // deep sheltered galleries: hauling/queen survive raids.
    leaderboardValue: 210,
    energyReward: 80,
    blurb: 'Deep galleries linking every nest — sheltered hauling that never stops.',
  },
  {
    id: PROJECT.FungalSuperfarm,
    name: 'Fungal Superfarm',
    region: REGION.Garden,
    minPopulation: 36,
    laborNeeded: 2200,
    foodCost: 420,
    foodDividend: 9,
    bonusType: BONUS_TYPE.GeneDropRate, // cultivated mutagens: a wellspring of gene drops.
    leaderboardValue: 300,
    energyReward: 120,
    blurb: 'Cultivated fungal mats: abundant food and a wellspring of strange mutagens.',
  },
];

/** Human-readable name per PROJECT.* (order matches the enum). */
export const PROJECT_NAME: readonly string[] = PROJECT_DEFS.map((d) => d.name);
